import React, { useEffect, useState } from "react";
import { baseURL, UserProfileData } from "../../../content/content";
import InputComponent from "../../../component/input/Input";
import {
    createTheme,
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    ThemeProvider,
} from "@mui/material";
import profileImg from "../../../assets/icons/profile-image.svg";
import editIcon from "../../../assets/icons/edit-2.svg";
import deleteIcon from "../../../assets/icons/trush-2.svg";
import CommonButton from "../../../component/button/Button";
import Toast from "../../../component/toastify/Index";
import "../../../styles/pages/profile/profile.scss";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from "react-i18next";
import Heading from "../../../component/Heading/Heading";
import { getCustomerProfileDetails, updateCustomerProfileDetails } from "../../../services/customer-services/customerProfileService";
import useForm from "../../../hooks/useForm";
import CustomerChangePassword from "./change-password/ChangePassword";
import { useCommonContext } from "../../../context/common";
import { Grid } from '@mui/material';

const initialValue = {
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    language: "",
    profileImg: null
}

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 2000,
            xxl: 3000,
        },
    },
});

const CustomerProfile = () => {
    const { t } = useTranslation();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { setSelectedLanguage } = useCommonContext();

    const validateForm = (formData) => {
        const errors = {};

        UserProfileData?.Generally?.forEach((item) => {
            const fieldValue = formData[item.name];

            if (!fieldValue) {
                errors[item.name] = `${t(item.translationKey)} ${t("common_is_required")}`;
            } else {
                if (item.name === 'email' && !/^\S+@\S+\.\S+$/.test(fieldValue)) {
                    errors[item.name] = `${t('common_is_valid_email')}`;
                } else if (item.name === 'mobileNumber') {
                    if (fieldValue.length > 20) {
                        errors[item.name] = `${t('common_is_valid_mobile_number')}`;
                    }
                }
            }
        });

        return errors;
    };

    const { formData, errors, handleChange, handleSubmit, setFormData, setErrors } = useForm(
        initialValue,
        validateForm,
        "", "",
        setProfileImage,
        setPreviewUrl
    );

    const getAdminProfile = async () => {
        const response = await getCustomerProfileDetails();
        if (response?.isSuccess) {
            delete response.customerProfile.password;
            setFormData({ ...response.customerProfile });
            response?.customerProfile?.profileImage && setProfileImage(response?.customerProfile?.profileImage)
        }
    };

    useEffect(() => {
        getAdminProfile();
    }, []);

    const onSubmit = async (e) => {
        const formDetails = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (key !== "_id") {
                formDetails.append(key, value);
            }
        });

        const response = await updateCustomerProfileDetails(formDetails);
        if (response?.isSuccess) {
            setSelectedLanguage(formData?.language)
            Toast(response.message, "success");
            getAdminProfile();
        }
    };

    const handleDelete = () => {
        setPreviewUrl(null);
        setProfileImage(null)
        setFormData((prevData) => ({
            ...prevData,
            profileImage: null,
        }));
    };

    const handleReset = () => {
        setFormData(initialValue);
        setErrors({});
        setProfileImage(null);
        setPreviewUrl(null);
    };

    const handleOpen = () => {
        setIsModalOpen(true);
    };

    return (
        <ThemeProvider theme={theme}>
            <div className="copony-profile user-profile">

                <CustomerChangePassword isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />

                <div className="copony-profile-heading">
                    <Heading text={t('module_profile_settings')} />
                </div>
                <form className="company-profile-form" onSubmit={(e) => handleSubmit(e, onSubmit)}>
                    <div>
                        <div className="copony-profile-subheading">
                            <h3 className="Generally">{t('profile_generally')}</h3>
                        </div>
                        <Grid container>
                            <Grid item size={{ xs: 12, md: 8, xl: 9, xxl: 10 }} className="generally-col-one">
                                <Grid container rowSpacing={2} columnSpacing={3}>
                                    {UserProfileData?.Generally?.map((item, index) => (
                                        <Grid item size={{ xs: 12, lg: 6 }} key={index}>
                                            {
                                                item.type === "select" ? (
                                                    <FormControl
                                                        fullWidth
                                                        error={!!errors[item.name]}
                                                    >
                                                        <FormLabel
                                                            className="label"
                                                            sx={{
                                                                paddingTop: "1.25rem",
                                                                paddingBottom: "10px",
                                                                display: "inline-block",
                                                                color: "#000",
                                                            }}
                                                        >
                                                            {t(item.translationKey)}
                                                        </FormLabel>
                                                        <Select
                                                            className="select-input"
                                                            fullWidth
                                                            value={formData[item?.name] || ""}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFormData({
                                                                    ...formData,
                                                                    [item?.name]: event.target.value,
                                                                });
                                                            }}
                                                            displayEmpty
                                                            inputProps={{
                                                                name: item?.name,
                                                                IconComponent: () => (
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            paddingRight: "10px",
                                                                        }}
                                                                    >
                                                                        <ExpandMoreIcon
                                                                            sx={{ fontSize: "30px", color: "#33333399" }}
                                                                        />
                                                                    </span>
                                                                ),
                                                            }}
                                                            style={{
                                                                padding: "0",
                                                                borderRadius: "10px",
                                                                border: "1px solid #e3e3e391",
                                                            }}
                                                        >
                                                            {/* Placeholder */}
                                                            <MenuItem value="" disabled>
                                                                {`${t('profile_placeholder_language')}`}
                                                            </MenuItem>

                                                            {/* Options */}
                                                            {item?.options?.map((option, i) => (
                                                                <MenuItem key={i} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>

                                                        {errors[item.name] && (
                                                            <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors[item.name]}</FormHelperText>
                                                        )}
                                                    </FormControl>

                                                ) :
                                                    <InputComponent
                                                        type={item.type}
                                                        borderColor="E3E3E3"
                                                        border="2px solid #E3E3E3"
                                                        padding="10px 12px"
                                                        placeholder={t(item.placeholderTranslationKey)}
                                                        label={t(item.translationKey)}
                                                        className="input-field"
                                                        name={item.name}
                                                        value={formData[item.name] || ""}
                                                        onChange={handleChange}
                                                        required
                                                        error={!!errors[item.name]}
                                                        helperText={errors[item.name]}
                                                    />
                                            }
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                            <Grid item size={{ xs: 12, md: 4, xl: 3, xxl: 2 }} className="generally-col-two">
                                <div className="flex justify-center profile-height">
                                    <div className="company-data">
                                        <div className='company-logos w-100'>
                                            <label htmlFor="upload-profile-image" className="cursor-pointer">
                                                <img src={previewUrl ? previewUrl : profileImage ? `${baseURL}/upload/profileImages/customer/${profileImage}` : profileImg} alt="profile-image" className='w-100' />
                                            </label >

                                            {/* </div> */}
                                            < div className='action-icon' >
                                                <div className='edit-icon'>
                                                    <label htmlFor="upload-profile-image" className="cursor-pointer">
                                                        <img src={editIcon} alt="edit icon" className="edit profile-action-icons" />
                                                    </label>
                                                </div>
                                                <div className='delete-icon'>
                                                    <img src={deleteIcon} alt="edit icon" className="delete profile-action-icons" onClick={handleDelete} />
                                                </div>
                                            </div >
                                        </div >

                                        <InputComponent
                                            type="file"
                                            bordersize="0"
                                            id="upload-profile-image"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleChange}
                                            name="profileImage"
                                            error={!!errors["profileImage"]}
                                            helperText={errors["profileImage"]}
                                        />
                                    </div >
                                </div >
                            </Grid >
                        </Grid >

                    </div >
                    <div className="button-container">
                        <CommonButton type="reset" text={t('profile_change_password')} className="add-user-btn user-profile-btn change-password-btn" onClick={handleOpen} />
                        <CommonButton type="reset" text={t('user_clear_all')} bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);" className="add-user-btn user-profile-btn" onClick={handleReset} />
                        <CommonButton type="submit" text={t("profile_save_profile")} className="add-user-btn user-profile-btn" />
                    </div>
                </form >
            </div >
        </ThemeProvider>
    );
};

export default CustomerProfile;